import React from 'react';
import { Typography, Row, Col } from 'antd';
import { 
  ExperimentFilled, 
  ThunderboltFilled, 
  FlagFilled,
  TeamOutlined
} from '@ant-design/icons';
import SEO from '../components/SEO';

const { Title, Text, Paragraph } = Typography;

const About = () => {
  return (
    <div className="w-full max-w-6xl mx-auto px-6 py-12 md:py-24 animate-fade-in-up">
      <SEO 
        title="About Us - COMPRESS AI & Afi Labs"
        description="Learn about the mission behind COMPRESS AI: bringing extreme precision and high-performance image optimization to creative professionals worldwide."
        keywords={['about compress AI', 'afi labs inc mission', 'image compression technology team', 'creative agency image tools']}
      />
      
      <div className="text-center mb-16">
        <Title level={1} className="!text-white !text-5xl md:!text-6xl !font-bold tracking-tight mb-4 text-glow">
          OUR <span className="text-purple-400">MISSION</span>
        </Title>
        <Text className="text-purple-200/60 text-lg md:text-xl font-medium tracking-wide max-w-2xl block mx-auto">
          Built with extreme precision for high-performance creative agency workflows.
        </Text>
      </div>

      <Row gutter={[48, 48]} align="middle" className="mb-24">
        <Col xs={24} lg={12}>
          <div className="glass-panel-dark border-white/5 p-8 md:p-12 rounded-[2.5rem] relative overflow-hidden group">
            <div className="absolute bottom-[-20%] right-[-10%] w-32 h-32 bg-purple-600/10 blur-3xl rounded-full z-0 group-hover:bg-purple-600/20 transition-all duration-500"></div>
            <Title level={2} className="!text-white !font-bold mb-6">COMPRESS AI STORY</Title>
            <Paragraph className="text-slate-400 text-lg leading-relaxed mb-6">
              COMPRESS AI was born from a simple observation: high-quality images shouldn't slow down the web. As creative professionals ourselves, we needed a tool that combined bulk ZIP processing with extreme precision and real-time telemetry output.
            </Paragraph>
            <Paragraph className="text-slate-400 text-lg leading-relaxed mb-8">
              Developed by <span className="text-purple-400 font-bold">James La</span> at <a href="https://afi.io/" target="_blank" rel="noopener noreferrer" className="text-purple-400 font-bold hover:text-purple-300 transition-colors duration-300">Afi Labs Inc.</a>, our platform focuses on providing an enterprise-grade solution that’s accessible to everyone.
            </Paragraph>
          </div>
        </Col>
        
        <Col xs={24} lg={12}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="glass-panel-dark border-white/5 p-8 rounded-3xl hover:border-purple-500/20 transition-all duration-300 group">
              <ExperimentFilled className="text-purple-400 text-3xl mb-4 group-hover:scale-110 transition-transform duration-300" />
              <Title level={4} className="!text-white !m-0 !font-bold">Extreme Precision</Title>
              <Text className="text-slate-500 text-sm">Every pixel matters. Our COMPRESSAI engine ensures maximum quality retention.</Text>
            </div>
            
            <div className="glass-panel-dark border-white/5 p-8 rounded-3xl hover:border-indigo-500/20 transition-all duration-300 group">
              <ThunderboltFilled className="text-indigo-400 text-3xl mb-4 group-hover:scale-110 transition-transform duration-300" />
              <Title level={4} className="!text-white !m-0 !font-bold">Next-Gen Speed</Title>
              <Text className="text-slate-500 text-sm">Optimized for rapid workflows, processing hundreds of assets in seconds.</Text>
            </div>
            
            <div className="glass-panel-dark border-white/5 p-8 rounded-3xl hover:border-red-500/20 transition-all duration-300 group">
              <FlagFilled className="text-red-400 text-3xl mb-4 group-hover:scale-110 transition-transform duration-300" />
              <Title level={4} className="!text-white !m-0 !font-bold">Privacy First</Title>
              <Text className="text-slate-500 text-sm">Enterprise encryption ensures your assets are treated with maximum security.</Text>
            </div>
            
            <div className="glass-panel-dark border-white/5 p-8 rounded-3xl hover:border-emerald-500/20 transition-all duration-300 group">
              <TeamOutlined className="text-emerald-400 text-3xl mb-4 group-hover:scale-110 transition-transform duration-300" />
              <Title level={4} className="!text-white !m-0 !font-bold">Community Driven</Title>
              <Text className="text-slate-500 text-sm">Built to empower creators and agencies around the globe.</Text>
            </div>
          </div>
        </Col>
      </Row>
      
      <div className="text-center py-20 border-t border-white/5">
        <Title level={2} className="!text-white !font-bold mb-12">Powered by Innovation</Title>
        <div className="flex flex-wrap justify-center gap-12 md:gap-24 opacity-40 hover:opacity-60 transition-opacity duration-500 grayscale">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center">
              <ThunderboltFilled className="text-white text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-light tracking-widest !m-0">COMPRESSAI</Title>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center">
              <ExperimentFilled className="text-white text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-light tracking-widest !m-0">AFI LABS</Title>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
